/**
 * XY Electron Layout Lib
 *
 * manager layout data
 */

import {
  ILayout,
  IRoster,
  IRendererObj,
  ITemplateObj,
  ITempScreenInfo,
  ITempLayout,
  IConfInfo,
  IReqParams,
  SDKType,
  IPageInfo,
  LayoutModel,
  LayoutMode,
  TemplateModel,
} from './type/index';
import { TEMPLATE, RATE } from './tools/template';
import { XYSlaveRTC } from './clientSlave';
import { XYTimer } from './tools/interval';
import {
  LOCAL_VIEW_ID,
  EVENT,
  TemplateModelRangeMap,
  DEFAULT_LOCAL,
  DEFAULT_PAGE_INFO,
  DEFAULT_CONF_INFO,
  MAX_SIZE,
  SUCCESS_CODE,
} from './tools/enum';
import { XYRTC } from './client';
import Logger from './tools/logger';
import { debounce, getStatus } from './tools/utils';
import { Render } from './render/index';
import { TempValType, TerminalType, VideoState } from './type/enum.type';

/**
 * LayoutManager Lib
 *
 * @constructor sdk 接收原始传入的类的实例
 */
export default class LayoutManager {
  private sdk: XYRTC | XYSlaveRTC;
  // layout list
  private layout: ILayout[];
  // video-stream list
  private streams: IRoster[];
  // 渲染器列表
  // sourceId、interval、render的绑定关系
  private renderers: IRendererObj;
  // logger func
  private logger: Logger;
  // layout model
  private templateModel: TemplateModel;
  // layout mode switch range
  private layoutMode: LayoutMode;

  private pageInfo: IPageInfo;
  private disabledPage: boolean;
  // screen and template data
  private tempInfo: {
    templateObject: ITemplateObj;
    screenInfo: ITempScreenInfo;
    tempLayout: ITempLayout;
  };
  // 会议变化数据
  private confInfo: IConfInfo;
  private local: ILayout;
  private interval: number;
  private sdkType: SDKType;
  private NumCache: { [key: string]: number };
  // 是否正在全屏
  private forceFullScreenId: string;

  // 是否local在共享屏幕
  private isLocalShareContent: boolean;

  private cacheContentTemplateModel: TemplateModel;

  private isOnhold: boolean;

  public onDocumentChange: () => void;

  constructor(sdk: XYRTC | XYSlaveRTC, type: SDKType = SDKType.MAIN) {
    this.sdk = sdk;
    this.layout = [];
    this.streams = [];
    this.renderers = {};
    this.confInfo = DEFAULT_CONF_INFO;
    this.logger = sdk.logger;
    this.local = DEFAULT_LOCAL;

    this.interval = 66.66;

    // 宿主SDK类型，分主SDK、SlaveSDK
    this.sdkType = type;
    // 缓存所有计算数据
    this.NumCache = {};

    this.templateModel = this.sdk.config.templateModel || TemplateModel.SPEAKER;
    // 布局分类 1. NORMAL(不带content) 2. CONTENT(带content)
    this.layoutMode = LayoutMode.NORMAL;

    // 分页
    this.pageInfo = JSON.parse(JSON.stringify(DEFAULT_PAGE_INFO));

    this.disabledPage = false;

    this.forceFullScreenId = '';
    // 是否local正在共享content
    this.isLocalShareContent = false;
    // content布局在从0页切换其它分页时需要记录布局模式，方便切回到0页时恢复布局
    this.cacheContentTemplateModel = TemplateModelRangeMap.CONTENT[0];

    this.isOnhold = false;

    this.onScreenChangeEvent = this.onScreenChangeEvent.bind(this);
    this.onDocumentChange = debounce(this.onScreenChangeEvent, 150, 100);
  }

  public getLocal() {
    return this.local;
  }

  public setLocal(local) {
    this.local = local;
  }

  public getLayout() {
    return this.layout;
  }

  public getStreams() {
    return this.streams;
  }

  public getTemplateModel() {
    return this.templateModel;
  }

  public getConfInfo() {
    return this.confInfo;
  }

  public getForceFullScreenId() {
    return this.forceFullScreenId;
  }

  public setForceFullScreenId(id: string) {
    return (this.forceFullScreenId = id);
  }

  /**
   * 基于推送的ConfInfoChanged消息更新ConfInfo数据
   *
   * @public
   * @param { IConfInfo } e 会议信息
   */
  public setConfInfo(e: IConfInfo) {
    const { contentPartCount = 0, participantCount = 0, chairManUrl = '' } = e;

    const layoutMode = contentPartCount
      ? LayoutMode.CONTENT
      : LayoutMode.NORMAL;

    // 总页数
    const totalPage = Math.ceil(participantCount / MAX_SIZE);

    this.pageInfo.totalPage = totalPage;

    // 是否需要切换布局
    let isSwitchLayout = false;

    if (layoutMode !== this.layoutMode) {
      this.logger.log(
        `setConfInfo: layoutMode changed from ${this.layoutMode} to ${layoutMode}`
      );

      this.layoutMode = layoutMode;
      // 在0页，模式发生改变，要切换布局
      // 在分页，模式变为content，要切换布局，并重置到0页
      if (
        this.pageInfo.currentPage === 0 ||
        layoutMode === LayoutMode.CONTENT
      ) {
        this.logger.log(
          `setConfInfo: isSwitchLayout is set to true because currentPage=0 or layoutMode=CONTENT`
        );

        isSwitchLayout = true;
      }
    }

    if (
      !this.confInfo.chairManUrl &&
      this.isRemoteManUrl(chairManUrl) &&
      this.pageInfo.currentPage > 0
    ) {
      this.logger.log(
        `setConfInfo: isSwitchLayout is set to true because remote chairManUrl appear and currentPage>0`
      );
      // 出现主会场并且不是local且当且不在0页 则要切换布局，并重置到0页
      isSwitchLayout = true;
    }

    if (isSwitchLayout || this.isLocalShareContent) {
      // 本地共享，使用GALLERY
      this.templateModel = this.isLocalShareContent
        ? TemplateModel.GALLERY
        : TemplateModelRangeMap[this.layoutMode][0];

      this.logger.log(
        `setConfInfo: templateModel is set to ${this.templateModel} because isSwitchLayout=true or isLocalShareContent=true`
      );

      this.sdk.emitMessage(
        EVENT.RTC_TEMPLATE_MODEL_CHANGED,
        this.templateModel
      );

      this.pageInfo.currentPage = 0;

      this.logger.log(`setConfInfo: currentPage is set to 0`);
    }

    if (isSwitchLayout) {
      // 布局发生变化，退出全屏
      if (this.forceFullScreenId) {
        this.logger.log(`setConfInfo: exit full screen`);

        this.forceFullScreenId = '';
        this.sdk.emitMessage(
          EVENT.RTC_EVENT_FORCE_FULL_SCREEN,
          this.forceFullScreenId
        );
      }
    }
    // 人数发生变化，当前页数已经大于最大页数，则设置页码为最大页码
    if (totalPage < this.pageInfo.currentPage) {
      this.logger.log(
        `setConfInfo: totalPage ${totalPage} is less than currentPage ${this.pageInfo.currentPage}`
      );

      this.pageInfo.currentPage = totalPage;
    }

    this.sdk.emitMessage(EVENT.RTC_PAGE_INFO, this.pageInfo);

    this.confInfo = e;
  }

  /**
   * 设置是否是等候室状态
   * @param { boolean } isOnhold
   */
  public setIsOnhold(isOnhold: boolean) {
    this.logger.log(`setIsOnhold: isOnhold is set to ${isOnhold}`);

    const prevOnhold = this.isOnhold;

    this.isOnhold = isOnhold;

    if (!isOnhold && prevOnhold) {
      const { model } = this.sdk.getConfig();

      if (model === LayoutModel.CUSTOM) {
        return;
      }

      // 重置到初始数据
      const { contentPartCount } = this.confInfo;

      // 如果正在全屏就退出全屏
      if (this.forceFullScreenId) {
        this.logger.log(`setIsOnhold: exit full screen`);

        this.forceFullScreenId = '';
        this.sdk.emitMessage(
          EVENT.RTC_EVENT_FORCE_FULL_SCREEN,
          this.forceFullScreenId
        );
      }

      this.layoutMode = contentPartCount
        ? LayoutMode.CONTENT
        : LayoutMode.NORMAL;
      // 恢复layout模板模式
      this.templateModel = this.isLocalShareContent
        ? TemplateModel.GALLERY
        : TemplateModelRangeMap[this.layoutMode][0];

      this.logger.log(`setIsOnhold: layoutMode is set to ${this.layoutMode}`);
      this.logger.log(
        `setIsOnhold: templateModel is set to ${this.templateModel}`
      );

      this.sdk.emitMessage(
        EVENT.RTC_TEMPLATE_MODEL_CHANGED,
        this.templateModel
      );

      // 恢复分页到0页
      if (this.pageInfo.currentPage !== 0) {
        this.logger.log(`setIsOnhold: currentPage is set to 0`);

        this.pageInfo.currentPage = 0;
        this.sdk.emitMessage(EVENT.RTC_PAGE_INFO, this.pageInfo);
      }
    }

    this.calcRequestLayout();
  }

  /**
   * 获取等候室状态
   * @returns { boolean }
   */
  public getIsOnHold() {
    return this.isOnhold;
  }

  /**
   * 设置VideoStreams数据
   *
   * @public
   * @param { IRoster[] } streams video streams list data
   */
  public setStreams(streams: IRoster[]) {
    this.streams = streams;
  }

  private getKeyByCallUri(roster: IRoster) {
    const { isContent, callUri } = roster;
    const mediaGroupId = isContent ? 1 : 0;
    return callUri + mediaGroupId;
  }

  /**
   * 更新 Renderers Map 数据
   *
   * 更新原则：
   * 1. 不存在streams数据中，清理无效的renderers数据
   * 2. 存在streams数据中，且状态时非开启画面，则清理资源
   * 3. 存在streams数据中，更新并开启定时器获取数据
   *
   * @public
   */
  public updateRenderers() {
    const renderKeys = Object.keys(this.renderers);
    this.logger.log('updateRenderers current renderers keys: ', renderKeys);

    const { model } = this.sdk.getConfig();

    let isExistLocal = true;
    if (model === LayoutModel.AUTO) {
      isExistLocal = this.isLocalInTemplate();
    }

    const localRoster = this.local.roster;
    const localRenderKey = this.getKeyByCallUri(localRoster);

    renderKeys.forEach((key: string) => {
      const index = this.streams.findIndex(
        (item) => this.getKeyByCallUri(item) === key
      );

      if (index === -1) {
        const isLocal = key === localRenderKey;
        // 如果自定义布局，是无法分辨用户存在local, 这里默认有，否则clear会影响local的展示
        if (isLocal && isExistLocal) {
          this.updateSingleRenderer(localRoster);
        } else {
          XYTimer.clearInterval(key);
          delete this.renderers[key];
          this.logger.log('updateRenderers delete renderer key: ', key);
        }
      } else {
        this.updateSingleRenderer(this.streams[index]);
      }
    });

    this.logger.log(
      'updateRenderers updated renderers keys: ',
      Object.keys(this.renderers)
    );
  }

  /**
   * 根据视频是否开启更新单个的render
   * @param sourceId {string} roster
   * @param roster {IRoster}
   */
  private updateSingleRenderer(roster: IRoster) {
    const key = this.getKeyByCallUri(roster);
    const { state, isContent, sourceId } = roster;
    const renderer = this.renderers[key];
    let timer = renderer.timer;

    if (state === VideoState.Received) {
      if (!timer) {
        this.logger.log('updateRenderers create interval timer: ', key);
        timer = XYTimer.setInterval(
          key,
          this.drawBySourceId.bind(this, sourceId, key),
          this.interval
        );
      }
    } else if (timer) {
      this.logger.log('updateRenderers clear interval timer: ', key);
      XYTimer.clearInterval(key);
      timer = null;
    }

    this.renderers[key] = {
      ...renderer,
      isContent,
      timer,
    };
  }

  /**
   * create video steam layout func
   *
   * @public
   */
  public createVideoStreamLayout() {
    const streamsLen = this.streams.length;
    this.layout = [];
    let existLocal = true;
    const { model } = this.sdk.getConfig();

    // 只有auto判断
    if (model === LayoutModel.AUTO) {
      existLocal = this.isLocalInTemplate();
    }

    if (this.local && this.local.roster.callUri && existLocal) {
      this.layout = [this.local];
    }

    for (let i = 0; i < streamsLen; i++) {
      const { sourceId, callUri, isContent } = this.streams[i];
      const item: ILayout = {
        id: callUri + (isContent ? '1' : '0'),
        sourceId: sourceId,
        roster: this.streams[i],
        position: { width: 0, height: 0 },
        positionStyle: { top: '', left: '', width: '', height: '' },
      };

      this.layout.push(item);
    }

    // 强制全屏，如果当前全屏的layout退会，需要退出全屏模式，并重新请流
    if (this.forceFullScreenId) {
      const hasForceLayoutItem = this.layout.findIndex(
        (item) => item.id === this.forceFullScreenId
      );

      if (hasForceLayoutItem === -1) {
        this.forceFullScreenId = '';
        this.sdk.emitMessage(
          EVENT.RTC_EVENT_FORCE_FULL_SCREEN,
          this.forceFullScreenId
        );

        this.calcRequestLayout();
        return;
      }
    }

    this.createLayout();
  }

  /**
   * Create layout list
   *
   * @public
   */
  public createLayout() {
    this.logger.log('create layout', this.templateModel);

    const config = this.sdk.getConfig();

    // 从gallery切换到speaker时，需要切割layout，否则会导致没有position属性
    if (config.model === LayoutModel.AUTO) {
      // auto布局时，需要计算container容器信息
      // 此容器会将所有元素以16：9的比例铺满在屏幕上
      this.calcScreenInfo();

      const maxLen = this.tempInfo.templateObject.length;
      this.layout = this.layout.slice(0, maxLen);
      // auto布局时，需要计算每个item的位置，size信息
      this.layout = this.calcPositionInfo();
    }

    this.logger.log('emit create layout: ', this.layout);

    this.sdk.emitMessage(EVENT.RTC_EVENT_VIDEO_STREAMS, this.layout);
  }

  /**
   * 开始/结束分享content
   * @param { boolean } isShare
   */
  public setLocalShareContent(isShare: boolean) {
    this.isLocalShareContent = isShare;

    let needRequestLayout = false;
    // 1. 别人没有共享情况下，local开始共享 2. 结束共享 这两种情况 ConfInfoChanged无法察觉，需要手动请流
    if ((isShare && this.confInfo.contentPartCount === 0) || !isShare) {
      needRequestLayout = true;
    }

    // 结束共享如果正在全屏，退出 ==> 不太重要
    if (!isShare && this.forceFullScreenId) {
      this.forceFullScreenId = '';
      this.sdk.emitMessage(
        EVENT.RTC_EVENT_FORCE_FULL_SCREEN,
        this.forceFullScreenId
      );
    }

    if (needRequestLayout) {
      this.pageInfo.currentPage = 0;
      this.sdk.emitMessage(EVENT.RTC_PAGE_INFO, this.pageInfo);

      // 多路共享时，即使结束共享，远端仍然可能有content, 因此并不是只要结束共享，就一定是speaker布局
      this.templateModel = isShare
        ? TemplateModel.GALLERY
        : TemplateModel.SPEAKER;

      if (isShare) {
        this.templateModel = TemplateModel.GALLERY;
      } else if (!this.confInfo.contentPartCount) {
        this.templateModel = TemplateModel.SPEAKER;
      } else {
        this.layoutMode = LayoutMode.CONTENT;
        this.templateModel = TemplateModelRangeMap[LayoutMode.CONTENT][0];
      }

      this.sdk.emitMessage(
        EVENT.RTC_TEMPLATE_MODEL_CHANGED,
        this.templateModel
      );

      this.calcRequestLayout();
    }
  }

  /**
   * Dynamic calc layout container by documentBody size
   *
   * @private
   */
  private calcScreenInfo() {
    const config = this.sdk.getConfig();
    const { elementId = '', offset = [] } = config.container || {};
    const [t = 0, d = 0, l = 0, r = 0] = offset;

    const containerEle = document.getElementById(elementId) || document.body;

    const containerHeight = containerEle.clientHeight;
    const containerWidth = containerEle.clientWidth;

    const offsetHeight = Number(t) + Number(d);
    const offsetWidth = Number(l) + Number(r);

    const realContainerHeight = containerHeight - offsetHeight;
    const realContainerWidth = containerWidth - offsetWidth;

    const clientWidth =
      realContainerWidth > 0 ? realContainerWidth : containerWidth;
    const clientHeight =
      realContainerHeight > 0 ? realContainerHeight : containerHeight;
    let templateObject = TEMPLATE[this.templateModel];

    let localNum = this.local.id ? 1 : 0;

    // 是否存在Local
    const existLocal = this.isLocalInTemplate();

    if (!existLocal) {
      localNum = 0;
    }

    let size = this.streams.length + localNum;

    // 强制全屏显示特殊处理
    if (this.forceFullScreenId) {
      templateObject = TEMPLATE[TemplateModel.SPEAKER];
    }

    let templateRate = RATE;
    let tempLayout = [];
    // 第0页和第1页 或者正在forceLayout, 布局需要根据画面数量决定布局
    if (
      (size <= templateObject.length && this.pageInfo.currentPage <= 1) ||
      this.forceFullScreenId
    ) {
      tempLayout = JSON.parse(JSON.stringify(templateObject.temp[size]));
      templateRate = templateObject.rate[size];

      // content+local这种模式，因为存在local, 而模板中local是放在第二位的，需要换一下位置
      if (
        [TemplateModel.TWO_PIC_PIP, TemplateModel.TWO_PIC_SYMMETRIC].includes(
          this.templateModel
        ) &&
        this.confInfo.participantCount <= 1 &&
        !this.forceFullScreenId &&
        existLocal
      ) {
        [tempLayout[0], tempLayout[1]] = [tempLayout[1], tempLayout[0]];
      }
    } else {
      // 如果大于1页，不管多少画面，就是固定的九宫格布局，并且没有local, 因此展示画面按顺序展示，而不是local放到第1位
      tempLayout = JSON.parse(
        JSON.stringify(templateObject.temp[templateObject.length])
      );
      if (this.pageInfo.currentPage > 1) {
        tempLayout.splice(6, 0, tempLayout.shift());
      }
      templateRate = templateObject.rate[templateObject.length];
    }

    const rateClientWidth =
      Math.floor((clientHeight / templateRate) * 1000) / 1000;
    const rateClientHeight =
      Math.floor(clientWidth * templateRate * 1000) / 1000;

    let screenInfo = {
      width: clientWidth,
      height: clientHeight,
      isWidth: false,
      rateWidth: 0,
      rateHeight: 0,
    };

    if (clientHeight > rateClientHeight) {
      screenInfo = {
        ...screenInfo,
        rateWidth: clientWidth,
        rateHeight: rateClientHeight,
        isWidth: true,
      };
    } else {
      screenInfo = {
        ...screenInfo,
        rateWidth: rateClientWidth,
        rateHeight: clientHeight,
        isWidth: false,
      };
    }

    this.tempInfo = {
      screenInfo,
      tempLayout,
      templateObject,
    };

    this.logger.log('tempInfo: ', this.tempInfo);

    const { width, height, rateHeight, rateWidth } = screenInfo;

    // 向外推送screenInfo信息
    // 此处需要保证外部存在emitMessage方法
    this.sdk.emitMessage(EVENT.RTC_EVENT_SCREEN_INFO, {
      width,
      height,
      layoutWidth: rateWidth,
      layoutHeight: rateHeight,
    });
  }

  /**
   * Calculate layout position and size
   *
   * @private
   */
  private calcPositionInfo() {
    const nextPositionLayout = this.layout.map((item, index) => {
      const position = this.getPosition(index);
      const newPosition = {
        ...item,
        ...position,
      };

      return newPosition;
    });

    return nextPositionLayout;
  }

  /**
   * Get layout item position and size
   *
   * @private
   * @param { number } index layout item index
   */
  private getPosition(index: number) {
    const { rateWidth, rateHeight } = this.tempInfo.screenInfo;
    const [x, y, w, h] = this.tempInfo.tempLayout[index].position;
    const zIndex = this.tempInfo.tempLayout[index].zIndex;
    let layoutX = Math.round(rateWidth * x);
    let layoutY = Math.round(rateHeight * y);
    let layoutWidth = Math.round(rateWidth * w);
    let layoutHeight = Math.round(rateHeight * h);

    let positionStyle = {
      left: `${layoutX}px`,
      top: `${layoutY}px`,
      width: `${layoutWidth}px`,
      height: `${layoutHeight}px`,
      zIndex,
    };
    let position = {
      width: layoutWidth,
      height: layoutHeight,
    };

    return {
      position,
      positionStyle,
    };
  }

  /**
   * 计算请流数据
   *
   * 视频质量级别
   * people 720p 时：Low 表示15 fps，Normal和High 表示 30 fps
   * people 1080p时：Low 表示15 fps，Normal和High 表示 60 fps，（如果解码能力不够60，会被压低为30的）
   * content 720p 时：Low 表示5 fps， Normal和High 表示 30 fps
   * content 1080p时：Low 表示15 fps，Normal和High 表示 30 fps
   * @public
   * @property { 0 | 1 | 2 | 3 | 4 } resolution 0: 90, 1: 180, 2: 360, 3: 720, 4: 1080
   * @property { 0 | 1 | 2 } quality 0: low, 1: normal, 2: high
   */
  public calcRequestLayout() {
    this.logger.log('[LM]Calc request layout func');

    if (this.isOnhold) {
      this.logger.log(
        'calcRequestLayout: reqList is empty because isOnhold is true'
      );

      this.sdk.requestLayoutEx({
        pageIndex: 0,
        maxViewCountPerPage: 1,
        reqList: [],
      });
      return;
    }

    if (this.forceFullScreenId) {
      this.calcForceScreenRequestLayout();
      return;
    }

    const {
      chairManUrl = '',
      participantCount,
      contentPartCount,
    } = this.confInfo;
    // 去除主会场的请流数量
    // 真实请流的数量(0.1.17版本之后participantCount包含自己需要减去自己)
    // 如果监会入会，则participantCount不包含自己
    // const config = this.sdk.getConfig();
    // const localLen = config["isMonitor"] ? 0 : 1;

    let frames = [];
    const reqList = [];
    const currentPage = this.pageInfo.currentPage;
    const isRemoteManUrl = this.isRemoteManUrl();
    const total = participantCount + contentPartCount;
    const templateObj = TEMPLATE[this.templateModel];
    const templateInfo = templateObj.temp;

    let size = total > templateObj.length ? templateObj.length : total;
    const faceType = this.sdk.getFaceType() || 'NONE';
    const reqFaceDetect = faceType !== 'NONE';

    // 防止 isLocalShareContent 只有local
    // 共享屏幕时本地最多显示4画面
    if (this.isLocalShareContent && size > 4) {
      size = 4;

      this.logger.log('calcRequestLayout: size is set to 4');
    }

    if (isRemoteManUrl) {
      const max = contentPartCount + 2;
      size = max > size ? size : max;

      this.logger.log(
        'calcRequestLayout: size is set because isRemoteManUrl is true'
      );
    }

    frames = templateInfo[size];

    this.logger.log('calcRequestLayout: frames=', frames);

    frames.forEach((item, index) => {
      // 判断是否需要请流
      let needRequest = false;
      let callUri = '';

      if (item.type === TempValType.LOCAL) {
        // 共享视图、共享+演讲 这两种如果有主会场或者远端至少有一个人 将local换成主会场/p1
        if (
          (this.templateModel === TemplateModel.TWO_PIC_PIP ||
            this.templateModel === TemplateModel.TWO_PIC_SYMMETRIC) &&
          participantCount > 1
        ) {
          this.logger.log(
            `calcRequestLayout: index=${index}, local is replaced by chairManUrl/p1`
          );

          needRequest = true;
          callUri = isRemoteManUrl ? chairManUrl : '';
        }

        // 如果对称布局，且大于1页，则将local换成其他人
        if (this.templateModel === TemplateModel.GALLERY && currentPage > 1) {
          this.logger.log(
            `calcRequestLayout: index=${index}, local is replaced by other people`
          );

          needRequest = true;
          callUri = '';
        }
      } else {
        needRequest = true;
        // 如果是p1并且存在主会场，将p1替换成主会场
        callUri =
          item.type === TempValType.P1 && isRemoteManUrl ? chairManUrl : '';
      }

      if (needRequest) {
        reqList.push(
          this.getReqData({
            isContent: item.type === TempValType.CONTENT,
            callUri,
            resolution: item.resolution,
            quality: 0,
            reqFaceDetect,
          })
        );
      }
    });

    this.logger.log('[LM]Request layout list: ', reqList);

    this.requestLayout(reqList, reqList.length, currentPage);
  }

  private getReqData(reqObj) {
    const nextReqObj = JSON.parse(JSON.stringify(reqObj));
    const remoteMasterMap = this.sdk?.getRemoteMasterMap() || {};
    const remoteMasterMapKeys = Object.keys(remoteMasterMap);

    if (remoteMasterMapKeys.length) {
      const deviceID = remoteMasterMap[remoteMasterMapKeys[0]].deviceID;
      this.logger.log('in set deviceId: ', deviceID);

      nextReqObj['exDeviceID'] = deviceID;
    }

    return nextReqObj;
  }

  /**
   * 请求参会成员质量和分辨率数据的画面
   *
   * @public
   * @param reqList 请流列表
   * @param maxViewCount 每页最多可请求多少数据流，最大可设定位8位
   * @param pageIndex：页码，指定请求第几页数据
   */
  public requestLayout(
    reqList: any = [],
    maxViewCount: number,
    pageIndex: number = 0
  ) {
    let maxCount = 8;
    const { model } = this.sdk.getConfig();

    if (model === LayoutModel.AUTO) {
      maxCount = maxViewCount;
    } else {
      maxCount = maxViewCount > 8 ? 8 : maxViewCount;
    }

    // 截取前maxCount的数据内容去请求画面
    const sliceReqList = reqList.slice(0, maxCount);
    const reqParams: IReqParams = {
      pageIndex,
      maxViewCountPerPage: maxCount,
      reqList: sliceReqList,
    };

    this.logger.log('[LM]Request layout', reqParams, maxCount);

    this.sdk.requestLayoutEx(reqParams);
  }

  /**
   * Create local layout data
   *
   * 调用入口：主SDK
   * 当接收到 connected 状态时，需要调用此方法
   * 第三方如没有特殊需求，可不需调用，内部会自动调用
   *
   * @public
   */
  public createLocalRender() {
    this.logger.log('create local render');

    let loginInfo = { callUri: '', displayName: 'Local' };

    if (this.sdkType === SDKType.MAIN) {
      loginInfo = this.sdk.getLoginInfo();
    }

    const { callUri = '', displayName = 'Local' } = loginInfo;
    const config = this.sdk.getConfig();
    const { muteVideo, muteAudio } = config;
    const makeCallInfo = this.sdk.getMakeCallInfo();

    this.local = {
      id: callUri + '0',
      sourceId: LOCAL_VIEW_ID,
      roster: {
        displayName: makeCallInfo.displayName || displayName,
        isContent: false,
        state: muteVideo ? 1 : 5,
        sourceId: LOCAL_VIEW_ID,
        audioMute: muteAudio,
        callUri,
        isActiveSpeaker: false,
        dt: TerminalType.DESKTOP,
      },
      position: {
        width: 0,
        height: 0,
      },
      positionStyle: {
        top: '',
        left: '',
        width: '',
        height: '',
      },
    };
    this.layout = [this.local];

    this.setLocalLayoutStatus();

    this.createLayout();
  }

  /**
   * 切换分页
   * @param page 分页数字
   * @returns
   */
  public switchPage(targetPage: number) {
    const { currentPage, totalPage } = this.pageInfo;
    const { model } = this.sdk.getConfig();
    const { CONTENT } = LayoutMode;

    if (model === LayoutModel.CUSTOM) {
      this.logger.log(
        'switchPage: current is custom model, not support switch page'
      );

      return getStatus('XYSDK:960103');
    }

    // 必须是非负整数
    if (typeof targetPage !== 'number' || !/^\d+$/.test(String(targetPage))) {
      this.logger.log('switchPage: wrong parameter');

      return getStatus('XYSDK:960101', { targetPage });
    }

    if (totalPage < targetPage) {
      this.logger.log('switchPage: Exceeded maximum page number');

      return getStatus('XYSDK:960116', { targetPage });
    }

    // 无需处理
    if (targetPage === currentPage) {
      return;
    }

    // 取消全屏
    if (this.forceFullScreenId) {
      this.logger.log('switchPage: exit full screen');

      this.forceFullScreenId = '';
      this.sdk.emitMessage(EVENT.RTC_EVENT_FORCE_FULL_SCREEN, '');
    }

    // 切换分页可能也在切换布局
    if (targetPage > 0) {
      if (currentPage === 0 && this.layoutMode === CONTENT) {
        // content布局要记录一下
        this.cacheContentTemplateModel = this.templateModel;

        this.logger.log(
          'switchPage: cacheContentTemplateModel is set to ',
          this.templateModel
        );
      }

      if (this.templateModel !== TemplateModel.GALLERY) {
        this.templateModel = TemplateModel.GALLERY;

        this.logger.log('switchPage: templateModel is set to GALLERY');
      }
    } else if (this.layoutMode === CONTENT) {
      // content布局恢复到之前记录的布局
      this.templateModel = this.cacheContentTemplateModel;

      this.logger.log(
        'switchPage: templateModel is restored to ',
        this.templateModel
      );
    } else {
      this.templateModel = TemplateModel.SPEAKER;

      this.logger.log('switchPage: templateModel is set to SPEAKER');
    }

    this.sdk.emitMessage(EVENT.RTC_TEMPLATE_MODEL_CHANGED, this.templateModel);

    this.pageInfo.currentPage = targetPage;
    this.sdk.emitMessage(EVENT.RTC_PAGE_INFO, this.pageInfo);

    this.calcRequestLayout();
  }

  /**
   * 切换布局模式
   * @param {TemplateModel} templateModel 布局模式
   * @returns
   */
  public switchLayout(templateModel?: TemplateModel) {
    const { model } = this.sdk.getConfig();
    // 自定义布局不支持
    if (model === LayoutModel.CUSTOM) {
      this.logger.log(
        'switchLayout: current is custom model, not support switch layout'
      );

      return getStatus('XYSDK:960102');
    }

    // 如果正在共享屏幕，不支持切换布局
    if (this.isLocalShareContent) {
      this.logger.log(
        'switchLayout: Content sharing, not support switch layout'
      );

      return getStatus('XYSDK:960117');
    }

    if (templateModel) {
      // 判断templateModel是否合理，不合理return
      if (
        templateModel !== TemplateModel.GALLERY &&
        !TemplateModelRangeMap[this.layoutMode].includes(templateModel)
      ) {
        this.logger.log('switchLayout: Unreasonable layout switching');

        return getStatus('XYSDK:960118', { templateModel });
      }

      if (this.templateModel === templateModel) {
        return getStatus(SUCCESS_CODE);
      }
    }

    this.templateModel = templateModel ?? this.calcNextTemplateModel();
    this.sdk.emitMessage(EVENT.RTC_TEMPLATE_MODEL_CHANGED, this.templateModel);

    this.logger.log(
      'switchLayout: templateModel is set to ',
      this.templateModel
    );

    // 分页
    const current = this.templateModel === TemplateModel.GALLERY ? 1 : 0;
    if (this.pageInfo.currentPage !== current) {
      this.pageInfo.currentPage = current;
      this.sdk.emitMessage(EVENT.RTC_PAGE_INFO, this.pageInfo);

      this.logger.log('switchLayout: currentPage is set to ', current);
    }

    // 记录一下content布局
    if (
      this.templateModel === TemplateModel.GALLERY &&
      this.layoutMode === LayoutMode.CONTENT
    ) {
      this.cacheContentTemplateModel = this.templateModel;

      this.logger.log(
        'switchLayout: cacheContentTemplateModel is set to ',
        this.templateModel
      );
    }

    // 取消强制全屏
    if (this.forceFullScreenId) {
      this.logger.log('switchLayout: exit full screen');

      this.forceFullScreenId = '';
      this.sdk.emitMessage(EVENT.RTC_EVENT_FORCE_FULL_SCREEN, '');
    }

    this.calcRequestLayout();

    return getStatus(SUCCESS_CODE);
  }

  /**
   * 切换布局时，计算要切换到的布局模式
   * @returns { TemplateModel }
   */
  private calcNextTemplateModel() {
    const TemplateModelRange = TemplateModelRangeMap[this.layoutMode];
    const len = TemplateModelRange.length;

    // content模式切换到对称布局（这种属于直接指定布局，不在此方法中）
    const currentIndex = TemplateModelRange.indexOf(this.templateModel);

    if (currentIndex === -1) {
      if (
        this.templateModel === TemplateModel.GALLERY &&
        this.layoutMode === LayoutMode.CONTENT
      ) {
        return this.cacheContentTemplateModel;
      }
      return TemplateModelRange[0];
    }

    let nextIndex = currentIndex + 1;

    if (nextIndex > len - 1) {
      nextIndex = 0;
    }

    return TemplateModelRange[nextIndex];
  }

  /**
   * 判断当前布局模式是否包含local
   * @returns {boolean}
   */
  private isLocalInTemplate() {
    const { participantCount } = this.confInfo;

    if (this.isOnhold) {
      return false;
    }

    if (this.forceFullScreenId && this.forceFullScreenId !== this.local.id) {
      return false;
    }

    if (this.pageInfo.currentPage > 1) {
      return false;
    }

    if (this.pageInfo.currentPage === 0) {
      if (
        (this.templateModel === TemplateModel.TWO_PIC_PIP ||
          this.templateModel === TemplateModel.TWO_PIC_SYMMETRIC) &&
        participantCount > 1
      ) {
        // 存在p1, 所以local被替换调了
        return false;
      }

      if (this.templateModel === TemplateModel.CONTENT_ONLY) {
        // 共享全布局没有local
        return false;
      }
    }

    return true;
  }

  /**
   * 判断是否存在远端主会场
   * @param {string} [callUri]
   * @returns {boolean}
   */
  private isRemoteManUrl = (callUri?: string) => {
    const chairManUrl = callUri ?? this.confInfo.chairManUrl;
    return chairManUrl && chairManUrl !== this.local?.roster.callUri;
  };

  /**
   * 强制全屏时，只请一路流
   * @param id layout id
   */
  private calcForceScreenRequestLayout() {
    const id = this.forceFullScreenId;
    const info = this.layout.find((item: ILayout) => item.id === id);
    const roster = info.roster;
    const reqList = [];
    const isLocal = id === this.local.id;
    const faceType = this.sdk.getFaceType() || 'NONE';
    const reqFaceDetect = faceType !== 'NONE';

    if (!isLocal) {
      const { callUri, isContent } = roster;

      reqList.push({
        isContent,
        callUri,
        resolution: isContent ? 4 : 3,
        quality: isContent ? 2 : 1,
        reqFaceDetect,
      });
    }

    this.logger.log('forceLayout request layout: ', reqList);

    this.sdk.requestLayoutEx({ pageIndex: 0, maxViewCountPerPage: 1, reqList });
  }

  /**
   * 绑定sourceId和canvas，代理用户视频渲染
   *
   * @param sourceId sourceId，通过回调：EVENT.RTC_EVENT_VIDEO_STREAMS 返回给第三方
   * @param canvasEle canvas元素或者canvas element id，由第三方提供canvas元素，后续由sdk接管负责渲染
   */
  public setVideoRender(
    sourceId: string,
    canvasEle: HTMLCanvasElement | string
  ) {
    console.log('setVideoRender', sourceId, canvasEle);

    const { model } = this.sdk.getConfig();

    if (!sourceId || !canvasEle || typeof sourceId !== 'string') {
      this.logger.warn('setVideoRender: invalid setVideoRender params:', {
        sourceId,
        canvasEleType: typeof canvasEle,
      });

      return getStatus('XYSDK:960101', { sourceId, canvasEle });
    }

    const index = this.layout.findIndex((item) => item.sourceId === sourceId);

    this.logger.log('setVideoRender layout index: ', index);

    if (index > -1) {
      const currentRoster = this.layout[index].roster;
      const renderKey = this.getKeyByCallUri(currentRoster);
      const { isContent = false, state = VideoState.Received } = currentRoster;
      let { timer = null, render = null } = this.renderers[renderKey] || {
        timer: null,
        render: null,
      };

      // 自定义布局渲染local，因为默认local时存在的，但是如果local消失后又出现，而上一个local render对应的元素已经不存在
      // 因此需要重置render， 创建新元素对应的render
      if (model === LayoutModel.CUSTOM && sourceId === LOCAL_VIEW_ID) {
        this.logger.log('setVideoRender: custom layout reset local render');
        render = null;
      }

      this.logger.log(
        'setVideoRender set video renderer item: ',
        currentRoster
      );

      // 视频开启
      if (state === VideoState.Received && !timer) {
        timer = XYTimer.setInterval(
          renderKey,
          this.drawBySourceId.bind(this, sourceId, renderKey),
          this.interval
        );
        this.logger.log('setVideoRender create interval timer:', renderKey);
      }
      // 前提还是必须有视频开启
      if (!render || render.canvas.id !== canvasEle) {
        const canvas = this.getCanvasEle(canvasEle);

        if (!canvas) {
          this.logger.log('setVideoRender: invalid canvas id or element');

          return getStatus('XYSDK:960101', { sourceId, canvasEle });
        }

        render = this.getRender(canvas);
        this.logger.log('setVideoRender create renderer:', renderKey);
      }

      this.renderers[renderKey] = {
        isContent,
        render,
        timer,
      };

      this.logger.log('setVideoRender success set video renderer', renderKey);

      return getStatus(SUCCESS_CODE);
    } else {
      this.logger.warn(
        'setVideoRender: invalid sourceId params, not found in layouts'
      );

      return getStatus('XYSDK:960119', { sourceId });
    }
  }

  /**
   * 获取 canvas 元素
   *
   * @private
   * @param {(HTMLCanvasElement | string)} canvasEle canvas element or element id
   * @return {*}
   * @memberof LayoutManager
   */
  private getCanvasEle(canvasEle: HTMLCanvasElement | string) {
    let canvas;

    // canvas id 节点数据
    if (typeof canvasEle === 'string') {
      canvas = document.getElementById(canvasEle);

      if (!canvas) {
        this.logger.warn('invalid canvas id!');

        return false;
      }
    } else if (
      // 是否是Canvas元素
      typeof canvasEle === 'object' &&
      canvasEle.nodeName === 'CANVAS'
    ) {
      canvas = canvasEle;
    }

    return canvas;
  }

  /**
   * 定时器回调方法，30帧/s的回调频率用来获取frame并渲染
   *
   * @private
   */
  private drawBySourceId() {
    const arr = Array.prototype.slice.call(arguments);
    const sourceId = arr[0];
    const renderKey = arr[1];
    const { isContent, render } = this.renderers[renderKey];

    this.drawVideoFrame(render, sourceId, isContent);
  }

  /**
   * 切换Local摄像头，更新renderer器的状态、定时器状态
   *
   * 当为 true 时，关闭定时器
   * 当为 false 时，开启定时器
   *
   * @public
   * @param { boolean } status videoMute status: true/false
   */
  public updateLocalRenderer(status: boolean) {
    this.logger.log('updateLocalRenderer func invoke:', status);
    const localRoster = this.local.roster;
    const renderKey = this.getKeyByCallUri(localRoster);
    const render = this.renderers[renderKey];

    if (render) {
      let timer = render.timer;

      if (status && !!timer) {
        XYTimer.clearInterval(renderKey);

        timer = null;
      } else {
        timer = XYTimer.setInterval(
          renderKey,
          this.drawBySourceId.bind(this, LOCAL_VIEW_ID, renderKey),
          this.interval
        );
      }

      this.renderers[renderKey] = {
        ...render,
        isContent: true,
        timer,
      };
    }
  }

  /**
   * update local roster status
   *
   * @public
   */
  public setLocalLayoutStatus() {
    this.logger.log('setLocalLayoutStatus func invoke.');

    const { audioMute, displayName } = this.local.roster;
    const { audio, video, rename } = this.sdk.getLocalVideoStatus();

    this.logger.log('setLocalLayoutStatus audioMute', audioMute);
    this.logger.log('setLocalLayoutStatus audio', audio);

    if ((audio === 'mute' && !audioMute) || (audio === 'unmute' && audioMute)) {
      this.sdk.emitMessage(EVENT.RTC_EVENT_AUDIO_STATUS_CHANGED, {
        muteMic: audio,
      });
    }

    this.local.roster.state =
      video === 'muteVideo' ? VideoState.VideoMute : VideoState.Received;
    this.local.roster.audioMute = audio === 'mute';

    if (!!rename && rename !== displayName) {
      this.logger.log('setLocalLayoutStatus rename', rename);
      this.local.roster.displayName = rename;
    }

    // 如果开启了外接屏幕渲染，需要更新状态
    if (this.sdk && this.sdk.setExternalTimer) {
      this.sdk.setExternalTimer(this.local);
    }

    this.sdk.emitMessage(EVENT.RTC_EVENT_VIDEO_STREAMS, this.layout);
  }

  /**
   * 获取Video frame流数据，并通过webgl渲染
   *
   * @public
   * @param render renderer渲染器，由 getRender 方法返回
   * @param sourceId sourceId 资源id
   * @param isContent isContent 是否是content设备
   */
  public drawVideoFrame(render: Render, sourceId: string, isContent: boolean) {
    const {
      hasData = false,
      buffer,
      width,
      height,
      rotation,
    } = this.sdk.getVideoFrame(sourceId, isContent);

    // console.log('drawVideoFrame',sourceId,hasData,buffer)

    if (render && hasData) {
      render.draw(buffer, width, height, rotation);
    }
  }

  /**
   * Get Renderer 渲染器
   *
   * @public
   * @param { HTMLCanvasElement } canvasEle canvas元素
   */
  public getRender(canvasEle: HTMLCanvasElement) {
    if (canvasEle) {
      return new Render(canvasEle, this.NumCache);
    }

    this.logger.log('[getRender]: canvas element is null');

    return null;
  }

  /**
   * add screen resize event listener
   *
   * @public
   */
  public initWindowResizeEvent() {
    window.addEventListener('resize', this.onDocumentChange);
  }

  /**
   * remove screen resize event listener
   *
   * @private
   */
  private removeWindowResizeEvent() {
    window.removeEventListener('resize', this.onDocumentChange);
  }

  /**
   * re calculate layout when screen size change
   *
   * @private
   */
  private onScreenChangeEvent() {
    this.createLayout();
  }

  /**
   * 停止所有的渲染器
   *
   * @public
   */
  public stopAllVideoRender() {
    const renderKeys = Object.keys(this.renderers);

    this.logger.log('stopAllVideoRender renderKeys: ', renderKeys);

    renderKeys.map((key: string) => {
      XYTimer.clearInterval(key);
      delete this.renderers[key];
    });
  }

  /**
   * 清理所有资源
   *
   * @public
   */
  public destroy() {
    if (this.forceFullScreenId) {
      this.forceFullScreenId = '';
      this.sdk.emitMessage(EVENT.RTC_EVENT_FORCE_FULL_SCREEN, '');
    }

    // 停止所有的渲染
    this.stopAllVideoRender();
    // 清理所有定时器
    XYTimer.clearAll();
    // 清理所有的renderers数据
    this.renderers = {};
    this.streams = [];
    this.layout = [];
    this.local = DEFAULT_LOCAL;
    this.layoutMode = LayoutMode.NORMAL;
    this.templateModel = TemplateModel.SPEAKER;
    this.sdk.emitMessage(EVENT.RTC_TEMPLATE_MODEL_CHANGED, this.templateModel);
    this.isLocalShareContent = false;
    this.cacheContentTemplateModel = TemplateModelRangeMap.CONTENT[0];
    this.pageInfo = JSON.parse(JSON.stringify(DEFAULT_PAGE_INFO));
    this.disabledPage = true;
    this.isOnhold = false;
    this.sdk.emitMessage(EVENT.RTC_PAGE_INFO, this.pageInfo);
    this.removeWindowResizeEvent();
  }
}
